import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { isValidPhone } from '../utils/phone';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [otp, setOtp] = useState('');
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const { register, verifyOtp, resendOtp, isLoggedIn, isLoading } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, isLoading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    return phoneNumber.slice(0, 15);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({
      ...prev,
      phoneNumber: formatted,
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!isValidPhone(formData.phoneNumber)) {
      setError('Please enter a valid phone number for the selected country');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      };

      const response = await register(payload);
      setRegisteredEmail(formData.email);
      setShowOtpForm(true);
      setSuccess(response.message || 'Verification code sent to your email!');
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      await verifyOtp(registeredEmail, otp, formData.password);
      setSuccess('Email verified! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await resendOtp(registeredEmail);
      setSuccess(response.message || 'New verification code sent to your email!');
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 1) {
      const newOtp = otp.split('');
      newOtp[index] = value;
      setOtp(newOtp.join(''));

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setOtp(pastedData);
  };

  return (
    <div className="register-page">
      <Navbar />

      <div className="register-wrapper">
        <div className="register-card">
          {/* Decorative elements */}
          <div className="register-decor register-decor-1"></div>
          <div className="register-decor register-decor-2"></div>

          <div className="register-header">
            <div className="register-icon">
              <i className={`bi ${showOtpForm ? 'bi-shield-lock' : 'bi-person-plus'}`}></i>
            </div>
            <h2>{showOtpForm ? 'Verify Your Email' : 'Create Account'}</h2>
            <p>{showOtpForm ? 'Enter the 6-digit code sent to your email' : 'Join Tali Restaurants today'}</p>
          </div>

          {!showOtpForm ? (
            <form onSubmit={handleSubmit} className="register-form">
              {error && (
                <div className="register-alert register-alert-error">
                  <i className="bi bi-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}

              {/* Name Row */}
              <div className="register-row">
                <div className="register-field">
                  <label htmlFor="firstName">
                    <i className="bi bi-person"></i>
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="register-field">
                  <label htmlFor="lastName">
                    <i className="bi bi-person"></i>
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Email Field */}
              <div className="register-field">
                <label htmlFor="email">
                  <i className="bi bi-envelope"></i>
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="john.doe@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Phone Number Field */}
              <div className="register-field">
                <label htmlFor="phoneNumber">
                  <i className="bi bi-telephone"></i>
                  Phone Number
                </label>
                <PhoneInput
                  country={'eg'}
                  value={formData.phoneNumber}
                  onChange={(phone) => setFormData(prev => ({ ...prev, phoneNumber: '+' + phone }))}
                  inputProps={{
                    id: 'phoneNumber',
                    name: 'phoneNumber',
                    required: true,
                  }}
                  containerClass="register-phone-container"
                  inputClass="register-phone-input"
                  buttonClass="register-phone-button"
                  enableSearch={true}
                  searchPlaceholder="Search country..."
                  preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
                />
              </div>

              {/* Password Row */}
              <div className="register-row">
                <div className="register-field">
                  <label htmlFor="password">
                    <i className="bi bi-lock"></i>
                    Password
                  </label>
                  <div className="register-password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      placeholder="Min 8 characters"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="register-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="register-field">
                  <label htmlFor="confirmPassword">
                    <i className="bi bi-lock-fill"></i>
                    Confirm Password
                  </label>
                  <div className="register-password-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="register-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>
              </div>

              <button type="submit" className="register-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="register-spinner"></span>
                    Creating account...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus"></i>
                    Create Account
                  </>
                )}
              </button>

              <div className="register-divider">
                <span>or</span>
              </div>

              <div className="register-footer">
                <p>
                  Already have an account?{' '}
                  <Link to="/login">Sign in here</Link>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="register-form otp-form">
              {error && (
                <div className="register-alert register-alert-error">
                  <i className="bi bi-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="register-alert register-alert-success">
                  <i className="bi bi-check-circle"></i>
                  <span>{success}</span>
                </div>
              )}

              <div className="otp-sent-to">
                <i className="bi bi-envelope-check"></i>
                <span>Code sent to <strong>{registeredEmail}</strong></span>
              </div>

              <div className="otp-inputs">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[index] || ''}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    className="otp-input"
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              <button type="submit" className="register-btn" disabled={loading || otp.length !== 6}>
                {loading ? (
                  <>
                    <span className="register-spinner"></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle"></i>
                    Verify Email
                  </>
                )}
              </button>

              <button
                type="button"
                className="register-btn-secondary"
                onClick={handleResendOtp}
                disabled={loading}
              >
                <i className="bi bi-arrow-repeat"></i>
                Resend Code
              </button>

              <div className="register-footer">
                <p>
                  <button
                    type="button"
                    className="register-link-btn"
                    onClick={() => {
                      setShowOtpForm(false);
                      setOtp('');
                      setError('');
                      setSuccess('');
                    }}
                  >
                    <i className="bi bi-arrow-left"></i>
                    Back to registration
                  </button>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
